import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import confetti from 'canvas-confetti';
import { FiAward, FiArrowRight } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

interface CourseCompletionModalProps {
  isOpen: boolean;
  courseId: string;
  pathId: string;
  onClose: () => void;
}

const CourseCompletionModal: React.FC<CourseCompletionModalProps> = ({
  isOpen,
  courseId,
  pathId,
  onClose
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      // Create a more elaborate confetti effect
      const duration = 3000;
      const end = Date.now() + duration;

      const colors = ['#FFD700', '#FFA500', '#FF6347'];

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.8 },
          colors: colors
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.8 },
          colors: colors
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      }());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleViewBadge = () => {
    navigate(`/profile?badge=${courseId}&path=${pathId}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative bg-base-100/90 backdrop-blur-md rounded-lg max-w-md w-full overflow-hidden border border-primary/30 shadow-[0_0_40px_rgba(255,0,128,0.3)]">
        {/* Background circuit pattern */}
        <div className="absolute inset-0 bg-circuit-pattern opacity-5 pointer-events-none"></div>
        
        {/* Animated corner diagonal lines */}
        <div className="absolute top-0 left-0 w-24 h-24 overflow-hidden pointer-events-none">
          <div className="absolute -top-12 -left-12 w-24 h-24 rotate-45 bg-primary opacity-10"></div>
        </div>
        <div className="absolute bottom-0 right-0 w-24 h-24 overflow-hidden pointer-events-none">
          <div className="absolute -bottom-12 -right-12 w-24 h-24 rotate-45 bg-primary opacity-10"></div>
        </div>
        
        {/* Animated glowing lines */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="w-full h-1 bg-primary/30 absolute top-[10%] left-0 animate-pulse"></div>
          <div className="w-1 h-full bg-primary/30 absolute top-0 left-[15%] animate-pulse" style={{animationDelay: '0.5s'}}></div>
          <div className="w-full h-1 bg-primary/30 absolute bottom-[10%] left-0 animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="w-1 h-full bg-primary/30 absolute top-0 right-[15%] animate-pulse" style={{animationDelay: '1.5s'}}></div>
        </div>
        
        <div className="p-8 text-center relative z-10">
          {/* Trophy Icon with pulsing effect */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="absolute inset-0 bg-primary rounded-full animate-pulse-glow opacity-30"></div>
              <div className="relative w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center border border-primary/30">
                <FiAward className="w-14 h-14 text-primary" />
              </div>
              <div className="absolute -inset-2 bg-primary/5 rounded-full animate-ping opacity-30 z-0"></div>
            </div>
          </div>

          {/* Title with glitch effect */}
          <div className="glitch-container mb-4">
            <h2 
              data-text={t('learn.courseCompletion.congratulations')}
              className="text-3xl font-bold text-primary font-tech"
            >
              {t('learn.courseCompletion.congratulations')}
            </h2>
          </div>

          {/* Message */}
          <p className="text-base-content/80 mb-8 max-w-sm mx-auto">
            {t('learn.courseCompletion.message')}
          </p>

          {/* Buttons */}
          <div className="flex flex-col gap-4">
            <button
              onClick={handleViewBadge}
              className="btn btn-primary gap-2 shadow-lg neon-glow"
            >
              {t('learn.courseCompletion.viewBadge')}
              <FiArrowRight />
            </button>
            <button
              onClick={() => {
                onClose();
                navigate('/learn');
              }}
              className="btn btn-outline btn-primary"
            >
              {t('learn.courseCompletion.continue')}
            </button>
          </div>
        </div>
        
        {/* Animated badge sparkles */}
        <div className="absolute top-1/4 left-6 w-3 h-3 bg-yellow-400 rounded-full animate-ping opacity-30" style={{animationDuration: '3s'}}></div>
        <div className="absolute top-1/3 right-8 w-2 h-2 bg-primary rounded-full animate-ping opacity-30" style={{animationDuration: '2.5s'}}></div>
        <div className="absolute bottom-1/4 left-10 w-2 h-2 bg-cyan-400 rounded-full animate-ping opacity-30" style={{animationDuration: '2s'}}></div>
      </div>
    </div>
  );
};

export default CourseCompletionModal;
